import { useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userTrackingService } from '../services/userTrackingService';

export const useUserTracking = () => {
  const { currentUser } = useAuth();

  // Set user ID when user changes
  useEffect(() => {
    if (currentUser) {
      userTrackingService.setUserId(currentUser.uid);
    } else {
      userTrackingService.clearUserId();
    }
  }, [currentUser]);

  // Tracking functions
  const trackProductView = useCallback(async (
    productId: string, 
    productTitle?: string, 
    productPrice?: number, 
    productCategory?: string
  ) => {
    try {
      await userTrackingService.trackProductView(productId, productTitle, productPrice, productCategory);
    } catch (error) {
      // Removed debug log to prevent sensitive data exposure
    }
  }, []);

  const trackProductClick = useCallback(async (productId: string, productTitle?: string) => {
    try {
      await userTrackingService.trackProductClick(productId, productTitle);
    } catch (error) {
      // Removed debug log to prevent sensitive data exposure
    }
  }, []);

  const trackCategoryBrowse = useCallback(async (categoryId: string, categoryName: string) => {
    try {
      await userTrackingService.trackCategoryBrowse(categoryId, categoryName);
    } catch (error) {
      // Removed debug log to prevent sensitive data exposure
    }
  }, []);

  const trackSearchQuery = useCallback(async (query: string) => {
    try {
      await userTrackingService.trackSearchQuery(query);
    } catch (error) {
      // Removed debug log to prevent sensitive data exposure
    }
  }, []);

  const trackAddToCart = useCallback(async (
    productId: string, 
    productTitle?: string, 
    productPrice?: number
  ) => {
    try {
      await userTrackingService.trackAddToCart(productId, productTitle, productPrice);
    } catch (error) {
      // Removed debug log to prevent sensitive data exposure
    }
  }, []);

  const trackAddToWishlist = useCallback(async (productId: string, productTitle?: string) => {
    try {
      await userTrackingService.trackAddToWishlist(productId, productTitle);
    } catch (error) {
      // Removed debug log to prevent sensitive data exposure
    }
  }, []);

  const trackPurchase = useCallback(async (
    productId: string, 
    productTitle?: string, 
    productPrice?: number
  ) => {
    try {
      await userTrackingService.trackPurchase(productId, productTitle, productPrice);
    } catch (error) {
      // Removed debug log to prevent sensitive data exposure
    }
  }, []);

  // Get current user location
  const getUserLocation = useCallback(() => {
    return userTrackingService.getCurrentUserLocation();
  }, []);

  // Set user location from address
  const setUserLocationFromAddress = useCallback((address: { city?: string; district?: string; province?: string; country?: string }) => {
    userTrackingService.setUserLocationFromAddress(address);
  }, []);

  return {
    trackProductView,
    trackProductClick,
    trackCategoryBrowse,
    trackSearchQuery,
    trackAddToCart,
    trackAddToWishlist,
    trackPurchase,
    getUserLocation,
    setUserLocationFromAddress,
    isTrackingEnabled: !!currentUser
  };
};
